import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";

import {
    Table,
    TableContainer,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    Box,
    SkeletonText,
    Checkbox,
    Collapse,
    Tooltip
} from "@chakra-ui/react";
import { COLORS } from "@/common/constants";

const TablePrivilege: React.FC<any> = forwardRef(({ isLoading, initialDataList }, ref) => {
    const [dataList, setDataList] = useState(initialDataList || []);
    const [expandedRow, setExpandedRow] = useState<number | null>(null);

    const toggleRow = (index: number) => {
        setExpandedRow(expandedRow === index ? null : index);
    };

    const handleChangeCheckbox = (parentIndex: number, childIndex: number, field: string, value: boolean) => {
        setDataList((prevDataList: any[]) => {
            if (!Array.isArray(prevDataList)) return prevDataList;

            const updatedDataList = [...prevDataList];
            const parentItem = updatedDataList[parentIndex];

            if (parentItem && parentItem.children && parentItem.children[childIndex]) {
                const childItem = parentItem.children[childIndex];
                if (childItem[field]) {
                    childItem[field].value = value;
                }
            }

            return updatedDataList;
        });
    };

    const getCheckedPrivileges = () => {
        return dataList
            .flatMap((item: any) =>
                item.children?.flatMap((child: any) =>
                    Object.keys(child)
                        .filter((key) => child[key]?.value)
                        .map((key) => child[key].id)
                        .filter((id) => id)
                ) || []
            );
    };

    const getUncheckedPrivileges = () => {
        return dataList
            .flatMap((item: any) =>
                item.children?.flatMap((child: any) =>
                    Object.keys(child)
                        .filter((key) => child[key]?.value === false)
                        .map((key) => child[key].id)
                        .filter((id) => id)
                ) || []
            );
    };

    useImperativeHandle(ref, () => ({
        getCheckedPrivileges,
        getUncheckedPrivileges,
    }));

    useEffect(() => {
        setDataList(initialDataList);
    }, [initialDataList]);

    return (
        <TableContainer bgColor="#fff" borderRadius="13">
            <Box overflowX="auto">
                <Table variant="simple" width="100%">
                    <Thead>
                        <Tr>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} rowSpan={2} textTransform="none" fontSize={14} width="40%">Menu</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} colSpan={2} textTransform="none" textAlign="center" fontSize={14} width="15%">View</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} rowSpan={2} textTransform="none" textAlign="center" fontSize={14} width="15%">Create</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} rowSpan={2} textTransform="none" textAlign="center" fontSize={14} width="15%">Update</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} rowSpan={2} textTransform="none" textAlign="center" fontSize={14} width="15%">Delete</Th>
                        </Tr>
                        <Tr>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" textAlign="center" fontSize={14} width="15%">List</Th>
                            <Th color={COLORS.TABLE_HEADER.FOREGROUND} backgroundColor={COLORS.TABLE_HEADER.BACKGROUND} textTransform="none" textAlign="center" fontSize={14} width="15%">Detail</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {isLoading && (
                            <Tr>
                                <Td colSpan={6}>
                                    <SkeletonText mt="4" noOfLines={6} spacing="4" skeletonHeight="3" />
                                </Td>
                            </Tr>
                        )}
                        {!isLoading && dataList?.map((item: any, index: number) => (
                            <React.Fragment key={item.name}>
                                <Tr
                                    onClick={() => toggleRow(index)}
                                    _hover={{ bg: "gray.100", cursor: "pointer" }}
                                    bg={expandedRow === index ? "gray.200" : "white"}
                                >
                                    <Td width="25%" style={{ fontWeight: 'bold' }}>{item.name}</Td>
                                    <Td width="15%" textAlign="center"></Td>
                                    <Td width="15%" textAlign="center"></Td>
                                    <Td width="15%" textAlign="center"></Td>
                                    <Td width="15%" textAlign="center"></Td>
                                    <Td width="15%" textAlign="center"></Td>
                                </Tr>
                                <Tr>
                                    <Td colSpan={6} p={0}>
                                        <Collapse in={expandedRow === index} animateOpacity>
                                            <Table width="100%">
                                                <Tbody>
                                                    {item.children && item.children.map((child: any, childIndex: number) => (
                                                        <Tr key={childIndex} bg="gray.50">
                                                            <Td width="35%">
                                                                <span style={{ marginRight: '5px' }}>{child.name}</span>
                                                                {child.description && <Tooltip label={child.description} aria-label="Info Tooltip" ml={3}>?</Tooltip>}</Td>
                                                            <Td width="15%" textAlign="center">
                                                                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                                                    <Checkbox
                                                                        isChecked={child.view_list?.value || false}
                                                                        onChange={(e: any) => handleChangeCheckbox(index, childIndex, 'view_list', e.target.checked)}
                                                                        isDisabled={!child.view_list?.method}
                                                                    />
                                                                    {child.view_list?.messages && <Tooltip label={child.view_list?.messages} aria-label="Info Tooltip">?</Tooltip>}
                                                                </div>
                                                            </Td>
                                                            <Td width="15%" textAlign="center">
                                                                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                                                    <Checkbox
                                                                        isChecked={child.view_detail?.value || false}
                                                                        onChange={(e: any) => handleChangeCheckbox(index, childIndex, 'view_detail', e.target.checked)}
                                                                        isDisabled={!child.view_detail?.method}
                                                                    />
                                                                    {child.view_detail?.messages && <Tooltip label={child.view_detail?.messages} aria-label="Info Tooltip">?</Tooltip>}
                                                                </div>
                                                            </Td>
                                                            <Td width="15%" textAlign="center">
                                                                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                                                    <Checkbox
                                                                        isChecked={child.create?.value || false}
                                                                        onChange={(e: any) => handleChangeCheckbox(index, childIndex, 'create', e.target.checked)}
                                                                        isDisabled={!child.create?.method}
                                                                    />
                                                                    {child.create?.messages && <Tooltip label={child.create?.messages} aria-label="Info Tooltip">?</Tooltip>}
                                                                </div>
                                                            </Td>
                                                            <Td width="15%" textAlign="center">
                                                                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                                                    <Checkbox
                                                                        isChecked={child.update?.value || false}
                                                                        onChange={(e: any) => handleChangeCheckbox(index, childIndex, 'update', e.target.checked)}
                                                                        isDisabled={!child.update?.method}
                                                                    />
                                                                    {child.update?.messages && <Tooltip label={child.update?.messages} aria-label="Info Tooltip">?</Tooltip>}
                                                                </div>
                                                            </Td>
                                                            <Td width="15%" textAlign="center">
                                                                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                                                                    <Checkbox
                                                                        isChecked={child.delete?.value || false}
                                                                        onChange={(e: any) => handleChangeCheckbox(index, childIndex, 'delete', e.target.checked)}
                                                                        isDisabled={!child.delete?.method}
                                                                    />
                                                                    {child.delete?.messages && <Tooltip label={child.delete?.messages} aria-label="Info Tooltip">?</Tooltip>}
                                                                </div>
                                                            </Td>
                                                        </Tr>
                                                    ))}
                                                </Tbody>
                                            </Table>
                                        </Collapse>
                                    </Td>
                                </Tr>
                            </React.Fragment>
                        ))}
                    </Tbody>
                </Table>
            </Box>
        </TableContainer>
    );
});

export default TablePrivilege;
